import { useState, useEffect, useRef } from 'react';

interface FacebookBrowserDetectionResult {
  isFacebookBrowser: boolean;
  isButtonVisible: boolean;
  normalButtonRef: React.RefObject<HTMLDivElement>;
}

export const useFacebookBrowserDetection = (): FacebookBrowserDetectionResult => {
  const [isFacebookBrowser, setIsFacebookBrowser] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(true);
  const normalButtonRef = useRef<HTMLDivElement>(null);

  // Detect Facebook browser on component mount
  useEffect(() => {
    const userAgent = navigator.userAgent || '';
    const isFBBrowser = userAgent.includes('FBAN') || userAgent.includes('FBAV') || userAgent.includes('FB_IAB');
    setIsFacebookBrowser(isFBBrowser);
  }, []);

  // Add intersection observer to check if the normal button is visible
  useEffect(() => {
    if (!isFacebookBrowser || !normalButtonRef.current) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsButtonVisible(entry.isIntersecting);
      },
      { 
        threshold: 0.5, // Button is considered visible if at least 50% is in viewport
        rootMargin: '0px 0px -120px 0px' // Consider bottom area of viewport with larger margin
      }
    );
    
    observer.observe(normalButtonRef.current);
    
    // Add scroll event as fallback detection mechanism
    const handleScroll = () => {
      if (normalButtonRef.current) {
        const rect = normalButtonRef.current.getBoundingClientRect();
        // For fixed buttons, we mainly check if they're at the bottom of the screen
        // and not being overlapped by browser UI elements
        const viewportHeight = window.innerHeight;
        
        // In Facebook browsers with fixed positioning, check if the bottom area is available
        // The button is considered not visible if it would be covered by browser UI
        const bottomSpace = viewportHeight - rect.bottom;
        const isVisible = bottomSpace >= 60; // Need at least 60px of space at bottom
        
        if (isButtonVisible !== isVisible) {
          setIsButtonVisible(isVisible);
        }
      }
    };
    
    // Run on resize as well to catch viewport changes
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    
    // Also check periodically for Facebook browsers which may have UI elements appearing/disappearing
    const intervalCheck = setInterval(() => {
      if (document.visibilityState === 'visible') {
        handleScroll();
      }
    }, 1000);
    
    // Initial check with a delay to ensure rendering is complete
    setTimeout(handleScroll, 500);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      clearInterval(intervalCheck);
    };
  }, [isFacebookBrowser, isButtonVisible]);

  return {
    isFacebookBrowser,
    isButtonVisible,
    normalButtonRef
  };
}; 